import React, { useEffect, useState, useContext, useMemo } from 'react';
import { FiTruck, FiMap, FiAlertTriangle, FiClock } from 'react-icons/fi';
import AuthContext from '../context/AuthContext';
import useVehicleTracking from '../hooks/useVehicleTracking';

// Components
import StatCard from '../components/dashboard/StatCard';
import VehicleMap from '../components/map/VehicleMap';
import AlertsList from '../components/alerts/AlertsList';
import LoadingSpinner from '../components/common/LoadingSpinner';

// Create dummy data outside the component to avoid recreation
const generateDummyData = () => {
  const dummyVehicles = [
    {
      id: 'v1',
      name: 'Truck 101',
      registration: 'ABC-1234',
      currentPosition: { lat: 40.7128, lng: -74.006, timestamp: new Date().toISOString() },
      currentSpeed: 35,
      status: 'active',
      lastUpdate: new Date().toISOString()
    },
    {
      id: 'v2',
      name: 'Delivery Van 202',
      registration: 'XYZ-5678',
      currentPosition: { lat: 40.7148, lng: -74.006, timestamp: new Date().toISOString() },
      currentSpeed: 0,
      status: 'idle',
      lastUpdate: new Date().toISOString()
    },
    {
      id: 'v3',
      name: 'Fleet Car 303',
      registration: 'DEF-9012',
      currentPosition: { lat: 40.7118, lng: -74.009, timestamp: new Date().toISOString() },
      currentSpeed: 45,
      status: 'active',
      lastUpdate: new Date().toISOString()
    },
    {
      id: 'v4',
      name: 'Cargo Truck 404',
      registration: 'GHI-4567',
      currentPosition: { lat: 40.7250, lng: -74.020, timestamp: new Date().toISOString() }, // Outside geofences
      currentSpeed: 28,
      status: 'stopped', // Stopped status - will show as red
      lastUpdate: new Date().toISOString()
    }
  ];
  
  const dummyGeofences = [
    {
      id: 'g1',
      name: 'Downtown Zone',
      type: 'circle',
      center: { lat: 40.7128, lng: -74.006 },
      radius: 500, // meters
      color: '#1A5F7A',
      active: true
    },
    {
      id: 'g2',
      name: 'Warehouse Area',
      type: 'circle',
      center: { lat: 40.7148, lng: -74.009 },
      radius: 300, // meters
      color: '#FF5733',
      active: true
    }
  ];
  
  const dummyAlerts = [
    {
      id: 'a1',
      type: 'geofence_exit',
      vehicleId: 'v1',
      geofenceId: 'g1',
      vehicle: { id: 'v1', name: 'Truck 101' },
      geofence: { id: 'g1', name: 'Downtown Zone' },
      createdAt: new Date().toISOString(),
      message: 'Truck 101 exited Downtown Zone',
      severity: 'warning',
      acknowledged: false
    },
    {
      id: 'a2',
      type: 'speeding',
      vehicleId: 'v3',
      vehicle: { id: 'v3', name: 'Fleet Car 303' },
      createdAt: new Date().toISOString(),
      message: 'Fleet Car 303 exceeded speed limit',
      severity: 'error',
      acknowledged: false
    },
    {
      id: 'a3',
      type: 'geofence_exit',
      vehicleId: 'v4',
      geofenceId: 'g1',
      vehicle: { id: 'v4', name: 'Cargo Truck 404' },
      geofence: { id: 'g1', name: 'Downtown Zone' },
      createdAt: new Date().toISOString(),
      message: 'Cargo Truck 404 is outside all geofences',
      severity: 'error',
      acknowledged: false
    }
  ];
  
  return { dummyVehicles, dummyGeofences, dummyAlerts };
};

const Dashboard = () => {
  // Get user from context, but also check localStorage as backup
  const { user } = useContext(AuthContext);
  const [userName, setUserName] = useState('');
  const [stats, setStats] = useState({
    totalVehicles: 0,
    activeVehicles: 0,
    totalGeofences: 0,
    activeAlerts: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showGeofences, setShowGeofences] = useState(true);
  const [showAlerts, setShowAlerts] = useState(true);
  
  // Initialize data once at component mount
  const { initialVehicles, initialGeofences } = useMemo(() => {
    const { dummyVehicles, dummyGeofences } = generateDummyData();
    return { 
      initialVehicles: dummyVehicles,
      initialGeofences: dummyGeofences
    };
  }, []);
  
  // Store geofences state
  const [geofences, setGeofences] = useState(initialGeofences);
  
  // Use the memoized value for vehicle tracking with geofences
  const { vehicles, alerts, isConnected } = useVehicleTracking(initialVehicles, geofences);

  // Check localStorage for user data on component mount
  useEffect(() => {
    // First try to get user from localStorage
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        setUserName(userData.name);
        console.log('Dashboard: Found user name in localStorage:', userData.name);
      } else if (user?.name) {
        // Fallback to context user if available
        setUserName(user.name);
        console.log('Dashboard: Using user name from context:', user.name);
      }
    } catch (err) {
      console.error('Error parsing user data from localStorage:', err);
      // If localStorage parsing fails, use context user if available
      if (user?.name) {
        setUserName(user.name);
      }
    }
  }, [user]);

  useEffect(() => {
    // Fetch dashboard data
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Get our dummy data
        const { dummyVehicles, dummyGeofences, dummyAlerts } = generateDummyData();
        
        // Update stats only, not vehicles (which will cause rerenders)
        setStats({
          totalVehicles: dummyVehicles.length,
          activeVehicles: dummyVehicles.filter(v => v.status === 'active').length,
          totalGeofences: dummyGeofences.length,
          activeAlerts: dummyAlerts.filter(a => !a.acknowledged).length
        });
        
        // Initialize geofences
        setGeofences(dummyGeofences);
        
        setLoading(false);
      } catch (err) {
        setError('Failed to load dashboard data');
        setLoading(false);
        console.error('Error fetching dashboard data:', err);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10">
        <p className="text-ui-error">{error}</p>
        <button 
          className="mt-4 btn btn-primary" 
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome message */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Welcome, {userName || user?.name || 'User'}!</h1>
          <p className="text-gray-600">Here's what's happening with your fleet today</p>
        </div>
        
        {/* Connection status */}
        <div className="flex items-center text-sm">
          <span className={`inline-block w-3 h-3 rounded-full mr-2 ${isConnected ? 'bg-ui-success' : 'bg-ui-error'}`}></span>
          <span>{isConnected ? 'Connected' : 'Disconnected'}</span>
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Vehicles"
          value={stats.totalVehicles}
          icon={<FiTruck size={24} />}
          color="bg-primary"
        />
        <StatCard
          title="Active Vehicles"
          value={stats.activeVehicles}
          icon={<FiClock size={24} />}
          color="bg-ui-success"
        />
        <StatCard
          title="Geofences"
          value={stats.totalGeofences}
          icon={<FiMap size={24} />}
          color="bg-secondary"
        />
        <StatCard
          title="Active Alerts"
          value={stats.activeAlerts}
          icon={<FiAlertTriangle size={24} />}
          color="bg-ui-warning"
        />
      </div>
      
      {/* Map and Alerts */}
      <div className={`grid grid-cols-1 ${showAlerts ? 'lg:grid-cols-4' : ''} gap-6`}>
        {/* Map */}
        <div className={`${showAlerts ? 'lg:col-span-3' : ''} card`}>
          <div className="mb-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Vehicle Locations</h2>
              
              <div className="flex items-center space-x-4">
                {/* Geofence toggle */}
                <div className="flex items-center">
                  <span className="text-sm mr-2">Show Geofences</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={showGeofences} 
                      onChange={() => setShowGeofences(!showGeofences)} 
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>
                
                {/* Alerts toggle */}
                <div className="flex items-center">
                  <span className="text-sm mr-2">Alerts Panel</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={showAlerts} 
                      onChange={() => setShowAlerts(!showAlerts)} 
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-secondary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-secondary"></div>
                  </label>
                </div>
              </div>
            </div>
            
            {/* Vehicle status legend */}
            <div className="flex flex-wrap items-center mt-2 text-xs text-gray-600">
              <span className="mr-4">Vehicle Status:</span>
              <div className="flex items-center mr-3">
                <span className="inline-block w-3 h-3 rounded-full bg-green-500 mr-1"></span>
                <span>Active</span>
              </div>
              <div className="flex items-center mr-3">
                <span className="inline-block w-3 h-3 rounded-full bg-yellow-500 mr-1"></span>
                <span>Idle</span>
              </div>
              <div className="flex items-center mr-3">
                <span className="inline-block w-3 h-3 rounded-full bg-red-500 mr-1"></span>
                <span>Stopped</span>
              </div>
              <div className="flex items-center">
                <span className="inline-block w-3 h-3 rounded-full bg-gray-500 mr-1"></span>
                <span>Offline</span>
              </div>
            </div>
          </div>
          
          <div className="h-96">
            <VehicleMap 
              vehicles={vehicles} 
              geofences={showGeofences ? geofences : []} 
            />
          </div>
        </div>
        
        {/* Recent Alerts */}
        {showAlerts && (
          <div className="card">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Recent Alerts</h2>
              <button 
                className="text-gray-400 hover:text-gray-600"
                onClick={() => setShowAlerts(false)}
                title="Hide alerts panel"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <AlertsList alerts={alerts} compact />
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;